import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import i18n from "@/i18n";
import { validateResetToken, type ResetTokenError } from "@/services/edgeFunctionWrappers/passwordResetWrapper";

/**
 * Ce hook centralise la logique de vérification d'auth :
 * - Nouveau flux : lien propriétaire `?token=...` réutilisable pendant 24h,
 *   validé côté serveur (aucune session requise).
 * - Ancien flux (liens Supabase déjà envoyés) : token dans le hash, conservé
 *   tel quel pour rétrocompatibilité.
 * - Permet l'accès à /set-password même sans session.
 *
 * Usage : const { loading, isRecoveryMode, resetToken, linkError } = useAuthVerification();
 */
export const useAuthVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [tokenType, setTokenType] = useState<string | null>(null);
  const [session, setSession] = useState<any>(null);
  const [resetToken, setResetToken] = useState<string | null>(null);
  const [linkError, setLinkError] = useState<ResetTokenError | null>(null);


  useEffect(() => {
    // Pour le debug uniquement
    // console.log("[useAuthVerification] Hook initialized", {
    //   pathname: location.pathname,
    //   hash: location.hash ? `${location.hash.substring(0, 20)}...` : '[NONE]',
    //   currentUrl: window.location.href,
    //   search: location.search
    // });

    const handleAuth = async () => {
      try {
        // 1. Extraction des tokens de l'URL
        let hashSource = location.hash || '';
        let searchSource = location.search || '';
        const fullUrl = window.location.href;

        // Patch pour certains navigateurs qui ne mettent pas à jour location.hash
        if (fullUrl.includes('#') && !hashSource) {
          hashSource = `#${fullUrl.split('#')[1]}`;
        }

        // ---- Nouveau flux : code propriétaire valable 24h ----
        const customToken = new URLSearchParams(searchSource).get('token');
        if (customToken) {
          setIsRecoveryMode(true);
          setResetToken(customToken);
          const result = await validateResetToken(customToken);
          if (!result.valid) {
            setLinkError(result.reason ?? "invalid_token");
          }
          setLoading(false);
          return;
        }

        // ---- Ancien flux : erreurs renvoyées par Supabase dans le hash ----
        const cleanHashForError = hashSource.startsWith('#') ? hashSource.substring(1) : hashSource;
        if (cleanHashForError) {
          const errorParams = new URLSearchParams(cleanHashForError);
          const errorCode = errorParams.get('error_code') || errorParams.get('error');
          if (errorCode) {
            setIsRecoveryMode(true);
            setLinkError(errorCode.includes('expired') ? "expired" : "invalid_token");
            setLoading(false);
            return;
          }
        }


        let accessToken: string | null = null;
        let type: string | null = null;

        // Hash (cas classique des liens supabase)
        if (hashSource) {
          const cleanHash = hashSource.startsWith('#') ? hashSource.substring(1) : hashSource;
          const hashParams = new URLSearchParams(cleanHash);
          accessToken = hashParams.get('access_token');
          type = hashParams.get('type');
          // Fallback manual
          if (!accessToken) {
            const parts = cleanHash.split('&');
            for (const part of parts) {
              if (part.startsWith('access_token=')) accessToken = part.split('=')[1];
              if (part.startsWith('type=')) type = part.split('=')[1];
            }
          }
        }

        // Search (?access_token=...)
        if (!accessToken && searchSource) {
          const searchParams = new URLSearchParams(searchSource);
          accessToken = searchParams.get('access_token');
          type = type || searchParams.get('type');
        }

        // 2. Vérifie le mode recovery
        const isRecovery =
          type === "recovery" ||
          location.pathname.includes("set-password") ||
          location.pathname.includes("reset-password") ||
          fullUrl.includes("type=recovery");

        setToken(accessToken);
        setTokenType(type);
        setIsRecoveryMode(isRecovery);

        // 3. Session supabase : check si session existante
        const { data: { session: supaSession } } = await supabase.auth.getSession();
        setSession(supaSession);

        // 4. Redirection : on redirige si PAS de session ET PAS de token recovery ET page protégée
        const isOnPasswordPage =
          location.pathname.includes("set-password") ||
          location.pathname.includes("reset-password");
        if (!supaSession && !accessToken && !isOnPasswordPage) {
          toast.error(i18n.t("auth.reconnectRequired", { ns: "appui" }));
          navigate("/login", { replace: true });
          setLoading(false);
          return;
        }

        // Important : NE PAS faire de setSession ici si recovery !
        setLoading(false);

      } catch (err) {
        // Si bug, on redirige sauf sur page set-password
        if (!location.pathname.includes("set-password")) {
          toast.error(i18n.t("auth.authError", { ns: "appui" }));
          navigate("/login", { replace: true });
        }
        setLoading(false);
      }
    };

    handleAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, location]);

  return { loading, isRecoveryMode, token, tokenType, session, resetToken, linkError };
};
